// The GCflix API client. Same origin as the SPA, so cookies — including the media
// credential this sets — need no cross-site ceremony.

import type { Collection, Playback } from './types'

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new ApiError(res.status, await res.text())
  return (await res.json()) as T
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`api ${status}`)
    this.name = 'ApiError'
  }
}

export async function fetchCatalog(): Promise<Collection[]> {
  const r = await fetch('/api/catalog')
  const body = await json<{ collections: Collection[] }>(r)
  return body.collections ?? []
}

export async function fetchCollection(key: string): Promise<Collection> {
  return json<Collection>(await fetch(`/api/catalog/${encodeURIComponent(key)}`))
}

/**
 * Authorises playback and installs the media credential.
 *
 * The important side effect is invisible here: the response carries Set-Cookie headers
 * scoped to this asset's path, and every subsequent segment request is authorised by them
 * at the edge. Calling this again — which is what renewal does — refreshes those cookies
 * without interrupting the player.
 */
export async function authorizePlayback(contentId: string, gcstoreToken: string): Promise<Playback> {
  const res = await fetch(`/api/playback/${contentId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${gcstoreToken}` },
    credentials: 'same-origin',
  })
  return json<Playback>(res)
}

export async function saveProgress(
  contentId: string,
  gcstoreToken: string,
  body: { positionSeconds: number; furthestSeconds: number; watchedSeconds: number; completed: boolean },
): Promise<void> {
  await fetch(`/api/progress/${contentId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${gcstoreToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export interface TelemetryEvent {
  event: string
  positionSeconds: number
  renditionHeight?: number
  detail?: string
  occurredAt: string
}

export async function sendTelemetry(
  contentId: string,
  gcstoreToken: string,
  events: TelemetryEvent[],
): Promise<void> {
  if (events.length === 0) return
  // Telemetry must never be the reason a viewing breaks: failures are swallowed on
  // purpose.
  try {
    await fetch(`/api/telemetry/${contentId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${gcstoreToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
      keepalive: true, // survives the page being closed mid-flush
    })
  } catch {
    /* ignored */
  }
}
